import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CalendarClock,
  Clock,
  Contact,
  List,
  Loader2,
  Plus,
  Save,
  Trash2,
  Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../apiBase';
import { formatApiError } from '../lib/apiError';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Checkbox } from './ui/checkbox';

const DAY_LABELS = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' },
];

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Tous les jours' },
  { value: 'weekdays', label: 'Jours ouvrés (lun–ven)' },
  { value: 'weekly', label: 'Jours spécifiques de la semaine' },
];

const INTERVAL_OPTIONS = [
  { value: 15, label: 'Toutes les 15 minutes' },
  { value: 30, label: 'Toutes les 30 minutes' },
  { value: 60, label: 'Toutes les heures' },
  { value: 120, label: 'Toutes les 2 heures' },
  { value: 240, label: 'Toutes les 4 heures' },
];

const EMPTY_FORM = {
  label: '',
  scope: 'contacts',
  recurrence: 'daily',
  days_of_week: [],
  trigger_mode: 'fixed_times',
  times: ['08:00'],
  interval_minutes: 60,
  enabled: true,
};

function formatScheduleSummary(schedule) {
  const scopeLabel = schedule.scope === 'lists' ? 'Listes' : 'Contacts';
  const recurrence = RECURRENCE_OPTIONS.find((o) => o.value === schedule.recurrence);
  let recurrenceLabel = recurrence?.label || schedule.recurrence;
  if (schedule.recurrence === 'weekly' && schedule.days_of_week?.length) {
    const days = schedule.days_of_week
      .map((d) => DAY_LABELS.find((x) => x.value === d)?.label)
      .filter(Boolean)
      .join(', ');
    recurrenceLabel = days ? `Chaque ${days}` : recurrenceLabel;
  }
  let triggerLabel = '';
  if (schedule.trigger_mode === 'interval') {
    const opt = INTERVAL_OPTIONS.find((o) => o.value === schedule.interval_minutes);
    triggerLabel = opt?.label || `Toutes les ${schedule.interval_minutes} min`;
  } else {
    triggerLabel = (schedule.times || []).join(', ') || '—';
  }
  return { scopeLabel, recurrenceLabel, triggerLabel };
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

const SyncScheduling = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [syncLock, setSyncLock] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  const loadSchedules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sync/schedules`);
      setSchedules(data || []);
    } catch (err) {
      toast.error(formatApiError(err, 'Impossible de charger les programmations.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSyncLock = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sync/progress`);
      setSyncLock(data?.running ? data : null);
    } catch {
      // L'état du verrou n'est pas bloquant pour afficher les programmations.
    }
  }, []);

  useEffect(() => {
    loadSchedules();
    loadSyncLock();
  }, [loadSchedules, loadSyncLock]);

  const handleUnlock = async () => {
    if (
      !window.confirm(
        'Lever le verrou de synchronisation ? Utilisez cette action seulement si aucune synchronisation n’est réellement en cours.',
      )
    ) {
      return;
    }
    setUnlocking(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/sync/progress/unlock`);
      toast.success(data?.message || 'Le verrou a été levé.');
      setSyncLock(null);
      await loadSchedules();
    } catch (err) {
      toast.error(formatApiError(err, 'Impossible de lever le verrou.'));
    } finally {
      setUnlocking(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, times: ['08:00'], days_of_week: [] });
  };

  const startEdit = (schedule) => {
    setEditingId(schedule.id);
    setForm({
      label: schedule.label || '',
      scope: schedule.scope,
      recurrence: schedule.recurrence,
      days_of_week: [...(schedule.days_of_week || [])],
      trigger_mode: schedule.trigger_mode,
      times: schedule.times?.length ? [...schedule.times] : ['08:00'],
      interval_minutes: schedule.interval_minutes || 60,
      enabled: schedule.enabled !== false,
    });
  };

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const toggleDay = (day) => {
    setForm((prev) => {
      const set = new Set(prev.days_of_week || []);
      if (set.has(day)) set.delete(day);
      else set.add(day);
      return { ...prev, days_of_week: [...set].sort((a, b) => a - b) };
    });
  };

  const addTimeSlot = () => {
    setForm((prev) => ({ ...prev, times: [...(prev.times || []), '12:00'] }));
  };

  const updateTimeSlot = (index, value) => {
    setForm((prev) => {
      const times = [...(prev.times || [])];
      times[index] = value;
      return { ...prev, times };
    });
  };

  const removeTimeSlot = (index) => {
    setForm((prev) => {
      const times = (prev.times || []).filter((_, i) => i !== index);
      return { ...prev, times: times.length ? times : ['08:00'] };
    });
  };

  const buildPayload = () => {
    const payload = {
      label: form.label.trim() || null,
      scope: form.scope,
      recurrence: form.recurrence,
      trigger_mode: form.trigger_mode,
      enabled: form.enabled,
      timezone: 'Europe/Paris',
    };
    if (form.recurrence === 'weekly') {
      payload.days_of_week = form.days_of_week;
    } else {
      payload.days_of_week = [];
    }
    if (form.trigger_mode === 'fixed_times') {
      payload.times = form.times;
      payload.interval_minutes = null;
    } else {
      payload.times = [];
      payload.interval_minutes = Number(form.interval_minutes);
    }
    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.recurrence === 'weekly' && !form.days_of_week.length) {
      toast.error('Sélectionnez au moins un jour pour la récurrence hebdomadaire.');
      return;
    }
    if (form.trigger_mode === 'fixed_times' && !form.times.length) {
      toast.error('Ajoutez au moins un créneau horaire.');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await axios.put(`${API_URL}/api/sync/schedules/${editingId}`, payload);
        toast.success('Programmation mise à jour.');
      } else {
        await axios.post(`${API_URL}/api/sync/schedules`, payload);
        toast.success('Programmation créée.');
      }
      resetForm();
      await loadSchedules();
    } catch (err) {
      toast.error(formatApiError(err, 'Enregistrement impossible.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Supprimer cette programmation ?')) return;
    try {
      await axios.delete(`${API_URL}/api/sync/schedules/${scheduleId}`);
      toast.success('Programmation supprimée.');
      if (editingId === scheduleId) resetForm();
      await loadSchedules();
    } catch (err) {
      toast.error(formatApiError(err, 'Suppression impossible.'));
    }
  };

  const handleToggleEnabled = async (schedule) => {
    try {
      await axios.put(`${API_URL}/api/sync/schedules/${schedule.id}`, {
        enabled: !schedule.enabled,
      });
      await loadSchedules();
    } catch (err) {
      toast.error(formatApiError(err, 'Mise à jour impossible.'));
    }
  };

  const formTitle = editingId ? 'Modifier la programmation' : 'Nouvelle programmation';

  const statusBadge = useMemo(
    () => ({
      success: { label: 'Succès', variant: 'default' },
      partial: { label: 'Partiel', variant: 'secondary' },
      error: { label: 'Erreur', variant: 'destructive' },
      skipped: { label: 'Impossible de lancer la synchronisation', variant: 'outline' },
    }),
    [],
  );

  if (loading) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 lg:px-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CalendarClock className="h-5 w-5 text-primary" aria-hidden />
            {formTitle}
          </CardTitle>
          <CardDescription>
            Planifiez des synchronisations automatiques vers Exchange (fuseau Europe/Paris).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="schedule-label">Libellé (optionnel)</Label>
                <Input
                  id="schedule-label"
                  value={form.label}
                  onChange={(e) => updateForm({ label: e.target.value })}
                  placeholder="Ex. Sync contacts matin"
                />
              </div>
              <div className="space-y-2">
                <Label>Type de synchronisation</Label>
                <Select value={form.scope} onValueChange={(v) => updateForm({ scope: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contacts">
                      <span className="flex items-center gap-2">
                        <Contact className="h-4 w-4" aria-hidden />
                        Contacts
                      </span>
                    </SelectItem>
                    <SelectItem value="lists">
                      <span className="flex items-center gap-2">
                        <List className="h-4 w-4" aria-hidden />
                        Listes
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Récurrence</Label>
              <Select
                value={form.recurrence}
                onValueChange={(v) => updateForm({ recurrence: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.recurrence === 'weekly' && (
              <div className="space-y-2">
                <Label>Jours de la semaine</Label>
                <div className="flex flex-wrap gap-3">
                  {DAY_LABELS.map((day) => (
                    <label
                      key={day.value}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <Checkbox
                        checked={form.days_of_week.includes(day.value)}
                        onCheckedChange={() => toggleDay(day.value)}
                      />
                      {day.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Créneaux horaires</Label>
              <Select
                value={form.trigger_mode}
                onValueChange={(v) => updateForm({ trigger_mode: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed_times">Heures fixes (ex. 11:00, 12:00)</SelectItem>
                  <SelectItem value="interval">Intervalle régulier</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.trigger_mode === 'fixed_times' ? (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4" aria-hidden />
                  Heures d&apos;exécution
                </Label>
                {(form.times || []).map((time, index) => (
                  <div key={`time-${index}`} className="flex max-w-xs items-center gap-2">
                    <Input
                      type="time"
                      value={time}
                      onChange={(e) => updateTimeSlot(index, e.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={(form.times || []).length <= 1}
                      onClick={() => removeTimeSlot(index)}
                      aria-label="Supprimer ce créneau"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addTimeSlot}>
                  <Plus className="mr-2 h-4 w-4" aria-hidden />
                  Ajouter un créneau
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-w-md">
                <Label>Intervalle</Label>
                <Select
                  value={String(form.interval_minutes)}
                  onValueChange={(v) => updateForm({ interval_minutes: Number(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVAL_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch
                id="schedule-enabled"
                checked={form.enabled}
                onCheckedChange={(checked) => updateForm({ enabled: checked })}
              />
              <Label htmlFor="schedule-enabled">Programmation active</Label>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Save className="mr-2 h-4 w-4" aria-hidden />
                )}
                {saving ? 'Enregistrement…' : editingId ? 'Mettre à jour' : 'Créer'}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Annuler
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Programmations enregistrées</CardTitle>
          <CardDescription>
            {schedules.length
              ? `${schedules.length} programmation(s) configurée(s).`
              : 'Aucune programmation pour le moment.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {syncLock && (
            <Alert>
              <Unlock className="h-4 w-4" aria-hidden />
              <AlertTitle>Impossible de lancer la synchronisation</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>
                  Une autre synchronisation est déjà en cours, ou un verrou est resté bloqué
                  {syncLock.updated_at ? ` depuis le ${formatDateTime(syncLock.updated_at)}` : ''}.
                  {syncLock.label ? ` Dernier élément : ${syncLock.label}.` : ''}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleUnlock}
                  disabled={unlocking}
                >
                  {unlocking ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Unlock className="mr-2 h-4 w-4" aria-hidden />
                  )}
                  {unlocking ? 'Déverrouillage…' : 'Lever le verrou'}
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {schedules.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Créez une programmation ci-dessus pour lancer des synchronisations automatiques.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Libellé / type</TableHead>
                    <TableHead>Récurrence</TableHead>
                    <TableHead>Créneaux</TableHead>
                    <TableHead>Dernière exécution</TableHead>
                    <TableHead>Actif</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedules.map((schedule) => {
                    const summary = formatScheduleSummary(schedule);
                    const status = statusBadge[schedule.last_run_status];
                    return (
                      <TableRow key={schedule.id}>
                        <TableCell>
                          <div className="font-medium">
                            {schedule.label || summary.scopeLabel}
                          </div>
                          <div className="text-xs text-muted-foreground">{summary.scopeLabel}</div>
                        </TableCell>
                        <TableCell className="text-sm">{summary.recurrenceLabel}</TableCell>
                        <TableCell className="text-sm">{summary.triggerLabel}</TableCell>
                        <TableCell className="text-sm">
                          <div>{formatDateTime(schedule.last_triggered_at)}</div>
                          {status && (
                            <Badge variant={status.variant} className="mt-1 whitespace-normal text-left">
                              {status.label}
                            </Badge>
                          )}
                          {schedule.last_run_message && (
                            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                              {schedule.last_run_message}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={schedule.enabled !== false}
                            onCheckedChange={() => handleToggleEnabled(schedule)}
                            aria-label={`Activer la programmation ${schedule.label || schedule.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => startEdit(schedule)}
                            >
                              Modifier
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => handleDelete(schedule.id)}
                              aria-label="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SyncScheduling;
